import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { RightOutlined } from '@ant-design/icons';
import SiteBrand from '../../../../core/components/SiteBrand';
import ThemeToggle from '../../../../core/components/ThemeToggle';
import './legal.css';

export type LegalNavigationItem = {
  id: string;
  label: string;
  icon: React.ReactNode;
};

export type LegalSummaryItem = {
  title: string;
  description: string;
  icon: React.ReactNode;
};

type LegalPageProps = {
  embedded?: boolean;
  title: string;
  description: string;
  updatedAt: string;
  icon: React.ReactNode;
  navigation: readonly LegalNavigationItem[];
  summaryTitle: string;
  summaryItems: readonly LegalSummaryItem[];
  children: React.ReactNode;
};

const scrollToSection = (sectionId: string) => {
  document.getElementById(sectionId)?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
};

const LegalPage: React.FC<LegalPageProps> = ({
  embedded = false,
  title,
  description,
  updatedAt,
  icon,
  navigation,
  summaryTitle,
  summaryItems,
  children,
}) => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${title} | Jobs & Opportunities ATS`;

    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  return (
    <div
      className={`ats-legal-page${embedded ? ' ats-legal-page--embedded' : ''}`}
    >
      {!embedded ? (
        <header className="ats-legal-header">
          <div className="ats-legal-rail ats-legal-header__inner">
            <Link
              to="/login"
              className="ats-legal-header__brand"
              aria-label="Jobs and Opportunities ATS sign in"
            >
              <SiteBrand markSize={40} showTagline />
            </Link>
            <div className="ats-legal-header__actions">
              <ThemeToggle />
              <Link to="/login" className="ats-legal-sign-in">
                Sign in
              </Link>
            </div>
          </div>
        </header>
      ) : null}

      <main>
        <section className="ats-legal-hero" aria-labelledby="legal-page-title">
          <div className="ats-legal-rail ats-legal-hero__inner">
            <div className="ats-legal-hero__icon" aria-hidden="true">
              {icon}
            </div>
            <div>
              {!embedded ? (
                <nav className="ats-legal-breadcrumb" aria-label="Breadcrumb">
                  <Link to="/login">ATS</Link>
                  <RightOutlined aria-hidden="true" />
                  <span>Legal</span>
                </nav>
              ) : null}
              <h1 id="legal-page-title">{title}</h1>
              <p className="ats-legal-hero__description">{description}</p>
              <p className="ats-legal-hero__updated">
                Last updated: {updatedAt}
              </p>
            </div>
          </div>
        </section>

        <div className="ats-legal-rail ats-legal-layout">
          <aside className="ats-legal-navigation">
            <nav aria-label={`${title} sections`}>
              <p className="ats-legal-navigation__label">On this page</p>
              <ul>
                {navigation.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(item.id)}
                    >
                      <span aria-hidden="true">{item.icon}</span>
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          <article className="ats-legal-content">
            <section
              className="ats-legal-summary"
              aria-labelledby="legal-summary-title"
            >
              <h2 id="legal-summary-title">{summaryTitle}</h2>
              <div className="ats-legal-summary__grid">
                {summaryItems.map((item) => (
                  <div className="ats-legal-summary__item" key={item.title}>
                    <span
                      className="ats-legal-summary__icon"
                      aria-hidden="true"
                    >
                      {item.icon}
                    </span>
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
            {children}
          </article>
        </div>
      </main>

      {!embedded ? (
        <footer className="ats-legal-footer">
          <div className="ats-legal-rail ats-legal-footer__inner">
            <p>
              &copy; {new Date().getFullYear()} Jobs &amp; Opportunities. All
              rights reserved.
            </p>
            <nav aria-label="Legal">
              <Link to="/privacy">Private Policy</Link>
              <Link to="/terms">Terms and Conditions</Link>
              <Link to="/login">Sign in</Link>
            </nav>
          </div>
        </footer>
      ) : null}
    </div>
  );
};

export default LegalPage;
